import Badge from '@/app/common/components/Badge'
import { ACETable } from '@/app/common/components/Table/ACETable'
import { useAceTable } from '@/app/common/components/Table/useAceTable'
import { cn } from '@/common/utilities.ts'
import { DownIcon, UpIcon } from '@/common/icons/general.tsx'
import { useDatabusMessageLogs } from '@/queries/databus_message_logs/hooks.ts'
import { DatabusMessageLogSortBy } from '@/queries/databus_message_logs/requests.ts'
import { DatabusMessageLog } from '@/queries/databus_message_logs/schema.ts'
import { createColumnHelper } from '@tanstack/react-table'
import { useState } from 'react'
import { formatIsoDateToUsersTimezone } from '@/lib/utils'
import { Button } from '@/app/common/components/button/Button'
import { AppRoutePaths } from '@/routing/route_paths'
import { useNavigate } from 'react-router-dom'
import {
  getDatabusStatusColor,
  getDatabusProcessingStatus,
  formatDatabusStatus,
} from './utils'
import { useHasPrivilege } from '@/hook/useHasPrivilege'
import NotAccessPage from '@/pages/not_access/NotAccessPage'

const columnHelper = createColumnHelper<DatabusMessageLog>()

type MessageQueueLogsSortableColumnLabel =
  | 'Source System'
  | 'Entity'
  | 'Entity ID'
  | 'Action Performed'
  | 'Error'
  | 'Processed Date'
  | 'Created At'

const columnLabelValDic: Record<
  MessageQueueLogsSortableColumnLabel,
  DatabusMessageLogSortBy
> = {
  'Source System': 'source_system',
  Entity: 'entity',
  'Entity ID': 'entity_id',
  'Action Performed': 'action',
  Error: 'error',
  'Processed Date': 'processed_at',
  'Created At': 'created_at',
}

function headerForColumn(
  name: MessageQueueLogsSortableColumnLabel,
  sortBy: DatabusMessageLogSortBy,
  sortOrder: 'asc' | 'desc',
  onSort: (column: MessageQueueLogsSortableColumnLabel) => void
) {
  const colVar = columnLabelValDic[name]
  const isActiveSort = colVar === sortBy

  return (
    <button
      type="button"
      className="inline-flex items-center justify-center gap-1 bg-transparent p-0 text-center"
      onClick={() => onSort(name)}
      aria-label={`Sort by ${name}`}
    >
      {name}
      <span className="inline-flex flex-col items-center leading-none">
        <UpIcon
          className={cn(
            'text-xs leading-none',
            isActiveSort && sortOrder === 'asc'
              ? 'text-gray-900'
              : 'text-gray-400'
          )}
        />
        <DownIcon
          className={cn(
            '-mt-1 text-xs leading-none',
            isActiveSort && sortOrder === 'desc'
              ? 'text-gray-900'
              : 'text-gray-400'
          )}
        />
      </span>
    </button>
  )
}

export default function MessageQueueLogsPage() {
  const aceTable = useAceTable()
  const navigate = useNavigate()

  const [sortBy, setSortBy] = useState<DatabusMessageLogSortBy>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const canReadMessageQueueLogs = useHasPrivilege('message_queue_logs_read')

  const logsQuery = useDatabusMessageLogs(
    {
      page: aceTable.currentPage,
      per_page: aceTable.recordsPerPage,
      search: aceTable.debouncedSearch,
      sort_by: sortBy,
      sort_order: sortOrder,
    },
    canReadMessageQueueLogs
  )

  if (!canReadMessageQueueLogs) {
    return <NotAccessPage />
  }

  const onSort = (column: MessageQueueLogsSortableColumnLabel) => {
    const nextSortBy = columnLabelValDic[column]
    const nextSortOrder =
      nextSortBy === sortBy ? (sortOrder === 'desc' ? 'asc' : 'desc') : 'asc'

    aceTable.setCurrentPage(1)
    setSortBy(nextSortBy)
    setSortOrder(nextSortOrder)
  }

  const columns = [
    columnHelper.accessor('source_system', {
      header: () => headerForColumn('Source System', sortBy, sortOrder, onSort),
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor('entity', {
      header: () => headerForColumn('Entity', sortBy, sortOrder, onSort),
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor('entity_id', {
      header: () => headerForColumn('Entity ID', sortBy, sortOrder, onSort),
      cell: (info) => (
        <div
          className="max-w-40 truncate whitespace-nowrap"
          title={info.getValue()}
        >
          {info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('action', {
      header: () => (
        <div className="flex justify-center">
          <div className="max-w-20 text-center leading-5">
            {headerForColumn('Action Performed', sortBy, sortOrder, onSort)}
          </div>
        </div>
      ),
      cell: (info) => (
        <div className="flex justify-center">
          <span className="max-w-20 whitespace-normal break-words text-center capitalize leading-5">
            {info.getValue()}
          </span>
        </div>
      ),
    }),
    columnHelper.display({
      id: 'processing_status',
      header: () => (
        <div className="flex justify-center">
          <div className="max-w-24 text-center leading-5">
            Processing Status
          </div>
        </div>
      ),
      cell: (info) => {
        const status = getDatabusProcessingStatus(info.row.original)

        return (
          <div className="flex justify-center">
            {status ? (
              <Badge
                semanticColor={getDatabusStatusColor(status)}
                className="max-w-24 whitespace-normal break-words text-center leading-5"
              >
                {formatDatabusStatus(status)}
              </Badge>
            ) : (
              '-'
            )}
          </div>
        )
      },
    }),
    columnHelper.accessor('error', {
      header: () => headerForColumn('Error', sortBy, sortOrder, onSort),
      cell: (info) => {
        const value = info.getValue()?.trim()

        return (
          <div
            className="max-w-48 truncate whitespace-nowrap"
            title={value ?? ''}
          >
            {value || '-'}
          </div>
        )
      },
    }),
    columnHelper.accessor('processed_at', {
      header: () => (
        <div className="flex justify-center">
          <div className="max-w-24 text-center leading-5">
            {headerForColumn('Processed Date', sortBy, sortOrder, onSort)}
          </div>
        </div>
      ),
      cell: (info) => {
        const value = info.getValue()

        return (
          <div className="flex justify-center">
            <span className="max-w-24 whitespace-normal text-center leading-5">
              {value ? formatIsoDateToUsersTimezone(value) : '-'}
            </span>
          </div>
        )
      },
    }),
    columnHelper.accessor('created_at', {
      header: () => (
        <div className="flex justify-center">
          <div className="max-w-24 text-center leading-5">
            {headerForColumn('Created At', sortBy, sortOrder, onSort)}
          </div>
        </div>
      ),
      cell: (info) => {
        const value = info.getValue()

        return (
          <div className="flex justify-center">
            <span className="max-w-24 whitespace-normal text-center leading-5">
              {value ? formatIsoDateToUsersTimezone(value) : '-'}
            </span>
          </div>
        )
      },
    }),
    columnHelper.display({
      id: 'details',
      header: () => <div className="flex justify-center">Details</div>,
      cell: (info) => {
        const log = info.row.original

        return (
          <div className="flex justify-center">
            <Button
              size="xs"
              onClick={() => {
                navigate(
                  AppRoutePaths.setup.message_queue_logs.details.replace(
                    ':messageLogId',
                    log.id.toString()
                  ),
                  { state: { messageLog: log } }
                )
              }}
            >
              Details
            </Button>
          </div>
        )
      },
    }),
  ]

  return (
    <div className="w-full h-full">
      <ACETable<DatabusMessageLog>
        data={logsQuery.data?.data ?? []}
        columns={columns}
        totalRows={logsQuery.data?.total ?? 0}
        currentPage={logsQuery.data?.current_page ?? aceTable.currentPage}
        recordsPerPage={aceTable.recordsPerPage}
        search={aceTable.search}
        onSearchChange={aceTable.onSearchChange}
        onPageChange={aceTable.onPageChange}
        onPerPageChange={aceTable.onPerPageChange}
        onRefresh={() => {
          void logsQuery.refetch()
        }}
        title="Message Queue Logs"
        searchPlaceholder="Search Logs"
        status={logsQuery.status}
        errorMessage="Error fetching logs."
        emptyMessage="No logs found."
        filter={null}
      />
    </div>
  )
}
